import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import CaptchaWidget from "@/components/base/CaptchaWidget";
import { submitForm, FORM_ENDPOINTS } from "@/services/forms";
import { firstProblem, validateFormData } from "@/utils/validation";
import { SITE } from "@/config/site";

const MAX_MESSAGE = 500;

const subjects = [
  "General enquiry",
  "Report an incorrect listing",
  "Listing plans & pricing",
  "Billing or invoice question",
  "Partnership / advertising",
  "Something else",
];

const contactCards = [
  {
    id: "email",
    icon: "ri-mail-line",
    label: "Email us",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    note: "We reply within one working day.",
  },
  {
    id: "phone",
    icon: "ri-phone-line",
    label: "Call us",
    value: SITE.phone,
    href: `tel:${SITE.phoneHref}`,
    note: "Mon–Sat, 9:30am – 6:30pm IST.",
  },
  {
    id: "visit",
    icon: "ri-map-pin-2-line",
    label: "Visit us",
    value: SITE.addressOneLine,
    note: "By appointment at our Mysore office.",
  },
];

const quickLinks = [
  { id: "q-1", icon: "ri-add-circle-line", label: "List your business", to: "/list-your-business" },
  { id: "q-2", icon: "ri-price-tag-3-line", label: "Compare listing plans", to: "/listing-plans" },
  { id: "q-3", icon: "ri-question-line", label: "Read the FAQs", to: "/faqs" },
  { id: "q-4", icon: "ri-alarm-warning-line", label: "Emergency services", to: "/emergency-services" },
];

export default function Contact() {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const problem = firstProblem(validateFormData(new FormData(form), { emailRequired: true }));
    if (problem) {
      setStatus("error");
      setFeedback(problem);
      return;
    }
    setStatus("submitting");
    setFeedback("");
    const result = await submitForm(
      FORM_ENDPOINTS.contact,
      new FormData(form),
      "Thanks for reaching out! Your message has been received and our team will get back to you shortly.",
      { captchaAction: "contact" },
    );
    if (result.ok) {
      setStatus("success");
      setFeedback(result.message);
      form.reset();
      setMessage("");
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  };

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent("Ramakrishna Nagar, Mysore, Karnataka 570023")}&output=embed`;
  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

  return (
    <>
      <Seo
        title="Contact Us — We're Here to Help"
        description="Get in touch with Mysore Yellow Pages. Ask about listings, plans, billing or report an issue with a business listing — our Mysore-based team replies within one working day."
        keywords="contact Mysore Yellow Pages, business directory support, list business contact"
        path="/contact"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "Contact" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            Contact us
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
            Questions about a listing, a plan or a payment? Send us a message and our local team will help.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          {contactCards.map((c) => (
            <div key={c.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <h2 className="mt-4 font-heading text-sm font-bold uppercase tracking-wide text-foreground-500">{c.label}</h2>
              {c.href ? (
                <a href={c.href} className="mt-1 block text-sm font-semibold text-foreground-950 hover:text-primary-700 break-words cursor-pointer">
                  {c.value}
                </a>
              ) : (
                <p className="mt-1 text-sm font-semibold text-foreground-950 leading-snug">{c.value}</p>
              )}
              <p className="mt-2 text-xs text-foreground-500">{c.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <h2 className="font-heading text-lg font-extrabold text-foreground-950">Send us a message</h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              Fill in the form and we'll get back to you within one working day.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className={labelClass}>Your name *</label>
                  <input id="name" name="name" required placeholder="Full name" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>Email *</label>
                  <input id="email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>Phone</label>
                  <input id="phone" name="phone" placeholder="+91 …" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="subject" className={labelClass}>Subject *</label>
                  <select id="subject" name="subject" required className={inputClass} defaultValue="">
                    <option value="" disabled>Select a topic</option>
                    {subjects.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="message" className={labelClass}>Message *</label>
                  <span className="text-[11px] text-foreground-400">{message.length}/{MAX_MESSAGE}</span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={MAX_MESSAGE}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                  placeholder="How can we help?"
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                className="form-aux-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <CaptchaWidget />

              {status === "success" && (
                <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600"></i>
                  <span>{feedback}</span>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{feedback}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Sending…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> Send message
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-500">
                By submitting you agree to our{" "}
                <Link to="/terms" className="font-semibold text-primary-700 hover:text-primary-800">terms &amp; conditions</Link>.
              </p>
            </form>
          </div>

          <aside className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <iframe title={`Map to ${SITE.name}`} src={mapSrc} className="h-64 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">Quick links</h2>
              <ul className="mt-3 space-y-1">
                {quickLinks.map((q) => (
                  <li key={q.id}>
                    <Link
                      to={q.to}
                      className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-foreground-700 hover:bg-background-50 hover:text-primary-700 cursor-pointer"
                    >
                      <i className={`${q.icon} text-primary-600`}></i> {q.label}
                      <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">Office</h2>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{SITE.addressOneLine}</p>
              <p className="mt-3 text-xs text-foreground-500">GSTIN: {SITE.gstin}</p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}